"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

// Branch-Commit-Daten für die Animation
// phase 0: zwei Commits auf main
// phase 1: feature branch öffnet + 2 Commits
// phase 2: merge zurück auf main + neuer Commit
const PHASES = [
  { label: "main", commits: 2, feature: 0, merged: false },
  { label: "feature", commits: 2, feature: 2, merged: false },
  { label: "merge", commits: 3, feature: 2, merged: true },
];

const MAIN_COLOR = "#3b82f6"; // blue-500
const FEATURE_COLOR = "#10b981"; // emerald-500

export default function Preview() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const current = PHASES[phase];

  // Commit-Positionen auf main: x evenly spaced
  const mainCommitXs = [60, 140, 220, 300];
  const featureCommitXs = [180, 255];
  const branchX = 160; // x where feature branches off
  const mergeX = 300; // x where it merges back
  const mainY = 80;
  const featureY = 36;

  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl p-4">
      <svg
        viewBox="0 0 360 116"
        className="w-full max-w-[340px]"
        aria-hidden="true"
      >
        {/* main branch line */}
        <line
          x1="30"
          y1={mainY}
          x2="330"
          y2={mainY}
          stroke={MAIN_COLOR}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.35"
        />

        {/* feature branch: arc from branchX/mainY to featureY and back */}
        <AnimatePresence>
          {phase >= 1 && (
            <motion.g
              key="feature-branch"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* branch-off line */}
              <line
                x1={branchX}
                y1={mainY}
                x2={featureCommitXs[0]}
                y2={featureY}
                stroke={FEATURE_COLOR}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.7"
              />
              {/* feature line */}
              <line
                x1={featureCommitXs[0]}
                y1={featureY}
                x2={featureCommitXs[1]}
                y2={featureY}
                stroke={FEATURE_COLOR}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.7"
              />
              {/* feature commits */}
              {featureCommitXs.map((cx, i) => (
                <motion.circle
                  key={i}
                  cx={cx}
                  cy={featureY}
                  r={6}
                  fill={FEATURE_COLOR}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: i * 0.15,
                    type: "spring",
                    stiffness: 300,
                  }}
                />
              ))}
              {/* labels */}
              <text
                x={featureCommitXs[0] - 8}
                y={featureY - 12}
                fontSize="9"
                fill={FEATURE_COLOR}
                opacity="0.8"
                fontFamily="monospace"
              >
                feature/login
              </text>
            </motion.g>
          )}
        </AnimatePresence>

        {/* merge line back to main */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.g
              key="merge-line"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <line
                x1={featureCommitXs[1]}
                y1={featureY}
                x2={mergeX}
                y2={mainY}
                stroke={FEATURE_COLOR}
                strokeWidth="2"
                strokeDasharray="4 3"
                strokeLinecap="round"
                opacity="0.7"
              />
              <circle cx={mergeX} cy={mainY} r={8} fill={MAIN_COLOR} />
              <text
                x={mergeX + 12}
                y={mainY + 4}
                fontSize="8"
                fill={MAIN_COLOR}
                opacity="0.8"
                fontFamily="monospace"
              >
                merge
              </text>
            </motion.g>
          )}
        </AnimatePresence>

        {/* main commits */}
        {mainCommitXs.slice(0, current.commits).map((cx, i) => (
          <motion.circle
            key={i}
            cx={cx}
            cy={mainY}
            r={i === current.commits - 1 && !current.merged ? 8 : 6}
            fill={MAIN_COLOR}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
        ))}

        {/* branch labels */}
        <text
          x="12"
          y={mainY + 4}
          fontSize="9"
          fill={MAIN_COLOR}
          opacity="0.7"
          fontFamily="monospace"
          fontWeight="bold"
        >
          main
        </text>
      </svg>
    </div>
  );
}
